import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Loader2, Building2, Users, TrendingUp, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const RANGES = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
];

function fmtDate(d) {
  if (!d) return "";
  const dt = new Date(d);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("30d");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/super-admin/analytics?range=${range}`)
      .then((r) => alive && setData(r.data))
      .catch((e) => alive && toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [range]);

  const chartData = useMemo(() => {
    if (!data?.views_by_day) return [];
    return data.views_by_day.map((d) => ({ date: fmtDate(d.date), views: d.views }));
  }, [data]);

  return (
    <AdminLayout>
      <div className="flex items-end justify-between mb-8 gap-4 flex-wrap" data-testid="admin-analytics-header">
        <div>
          <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Vawworld · Platform analytics</p>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">Network heartbeat</h1>
          <p className="text-white/50 mt-1 text-sm">Tenants, subscribers, top titles and revenue across every studio on the platform.</p>
        </div>
        <RangeSelector range={range} setRange={setRange} />
      </div>

      {loading || !data ? (
        <div className="grid place-items-center py-20 text-white/40 text-sm uppercase tracking-widest" data-testid="admin-analytics-loading">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="admin-analytics-kpis">
            <Kpi label="Active tenants" value={data.tenants_active.toLocaleString()} icon={<Building2 className="w-3.5 h-3.5" />} accent="#7C3AED" testid="kpi-tenants-active" />
            <Kpi label="Suspended" value={data.tenants_suspended.toLocaleString()} icon={<Building2 className="w-3.5 h-3.5" />} accent="#EF4444" testid="kpi-tenants-suspended" />
            <Kpi label="Subscribers" value={data.subscribers_total.toLocaleString()} icon={<Users className="w-3.5 h-3.5" />} accent="#10B981" testid="kpi-subscribers-total" />
            <Kpi label={`New subs · ${range}`} value={data.subscribers_new_in_range.toLocaleString()} icon={<TrendingUp className="w-3.5 h-3.5" />} accent="#F59E0B" testid="kpi-subscribers-new" />
          </div>

          {/* Revenue strip by currency */}
          <div className="bg-[#121212] border border-white/10 p-6 mb-8" data-testid="revenue-by-currency-card">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Revenue by currency</div>
            <h3 className="font-display font-bold text-lg mt-1 mb-5">{range} succeeded transactions</h3>
            {(data.revenue_by_currency || []).length === 0 ? (
              <EmptyState text="No succeeded transactions on the platform in this range." />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {data.revenue_by_currency.map((row) => (
                  <div key={row.currency} className="border border-white/10 p-5" data-testid={`revenue-cur-${row.currency}`}>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">{row.currency}</div>
                    <div className="mt-2 font-display font-bold text-3xl tracking-tight">{formatMoney(row.total, row.currency)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Views chart */}
          <div className="bg-[#121212] border border-white/10 p-6 mb-8" data-testid="chart-platform-views">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Views by day · platform</div>
                <h3 className="font-display font-bold text-lg mt-1">Total starts</h3>
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
                <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-amber-400" /> Views</span>
              </div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke="#ffffff14" vertical={false} />
                  <XAxis dataKey="date" stroke="#ffffff55" tickLine={false} axisLine={false} fontSize={11} />
                  <YAxis stroke="#ffffff55" tickLine={false} axisLine={false} fontSize={11} />
                  <Tooltip
                    contentStyle={{ background: "#0a0a0a", border: "1px solid #ffffff20", borderRadius: 0, fontSize: 12 }}
                    labelStyle={{ color: "#ffffff", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10 }}
                  />
                  <Line type="monotone" dataKey="views" stroke="#F59E0B" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top tenants by revenue */}
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="top-tenants-revenue-card">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Top tenants by revenue</div>
              <h3 className="font-display font-bold text-lg mt-1 mb-5">Network leaderboard</h3>
              {(data.top_tenants_by_revenue || []).length === 0 ? (
                <EmptyState text="No revenue across tenants in this range." />
              ) : (
                <div className="space-y-2">
                  {data.top_tenants_by_revenue.map((t, idx) => (
                    <Link
                      key={t.tenant_id}
                      to={`/s/${t.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-3 border border-white/5 hover:border-white/20 transition-colors"
                      data-testid={`top-tenant-${t.slug}`}
                    >
                      <div className="font-mono text-xs text-white/40 w-6">{String(idx + 1).padStart(2, "0")}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{t.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-0.5 inline-flex items-center gap-1">
                          /s/{t.slug} <ExternalLink className="w-3 h-3" />
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono">{formatMoney(t.revenue, t.currency)}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">{t.currency}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Top titles globally */}
            <div className="bg-[#121212] border border-white/10 p-6" data-testid="top-titles-global-card">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/40">Top titles globally</div>
              <h3 className="font-display font-bold text-lg mt-1 mb-5">Most-viewed across the network</h3>
              {(data.top_titles_global || []).length === 0 ? (
                <EmptyState text="No views recorded across tenants in this range." />
              ) : (
                <div className="space-y-2">
                  {data.top_titles_global.map((t, idx) => (
                    <Link
                      key={t.content_item_id}
                      to={`/s/${t.tenant_slug}/title/${t.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 p-3 border border-white/5 hover:border-white/20 transition-colors"
                      data-testid={`top-global-${t.slug}`}
                    >
                      <div className="font-mono text-xs text-white/40 w-6">{String(idx + 1).padStart(2, "0")}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{t.title}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-0.5">{t.tenant_slug}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-mono">{t.views}</div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">views</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}

function Kpi({ label, value, icon, accent, testid }) {
  return (
    <div className="bg-[#121212] border border-white/10 p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/45">
        <span style={{ color: accent }}>{icon}</span>
        {label}
      </div>
      <div className="mt-3 font-display font-bold text-2xl tracking-tight">{value}</div>
    </div>
  );
}

function RangeSelector({ range, setRange }) {
  return (
    <div className="inline-flex border border-white/10 bg-[#121212]" data-testid="admin-range-selector">
      {RANGES.map((r) => {
        const active = range === r.id;
        return (
          <button
            key={r.id}
            onClick={() => setRange(r.id)}
            className={`px-4 py-2 text-[10px] uppercase tracking-[0.25em] transition-colors ${active ? "text-black bg-amber-400" : "text-white/60 hover:text-white"}`}
            data-testid={`admin-range-${r.id}`}
          >
            {r.label}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="border border-dashed border-white/10 p-8 text-center text-white/45 text-sm">
      {text}
    </div>
  );
}
